package com.milta.feature.route.domain.entity

/**
 * A delivery not yet persisted to the backend (DOM-02). Held client-side while the manager is
 * assembling a new route on [com.milta.feature.route.presentation.CreateRouteScreen] — either
 * typed manually or produced by [ImportedDelivery.toDraft] after an Excel import — then sent as
 * the `deliveries` array of `POST /routes` or `POST /routes/{id}/deliveries`.
 */
data class DeliveryDraft(
    val recipientName: String,
    val address: String,
    val phoneNumber: String? = null,
    /** Raw "HH:mm" string, reviewed/corrected client-side (contract révisé DOM-02 — écran C/D du scan OCR). */
    val deadlineTime: String? = null,
    /** Optional, no range validation on the contract beyond >=1 — defaults server-side to 1 (MIL-35). */
    val packageCount: Int? = null,
    /** HERE validation outcome of [address] obtained at the draft stage (extraction / manual-entry
     * validation) — never a replacement address. Pending = not validated yet. */
    val geocodeStatus: GeocodeStatus = GeocodeStatus.Pending,
    /** Coordinates already resolved by HERE, sent back at creation so the server doesn't call HERE twice. */
    val latitude: Double? = null,
    val longitude: Double? = null,
    /** Retour 26/10 (R1/R11) — why HERE refused [address] (Failed only), null otherwise. */
    val geocodeFailureReason: GeocodeFailureReason? = null,
    /** Retour 29/10 — the address HERE matched ("Trouvée : …"), null when not reported (older data). */
    val resolvedAddress: String? = null,
)

/** Import review (new route): a draft is ready only with a non-blank address that HERE located
 * ([GeocodeStatus.Geocoded]) AND its GPS coordinates. A blank recipient name never blocks. */
/** Retour 26/10 (R2) — motif of a draft that blocks the next step, null when it doesn't. A blank
 * address (incomplete imported row) blocks too, shown by its own "adresse manquante" warning.
 * A "Geocoded" draft without its coordinates was never really validated: the server would check it
 * again at creation (and may refuse it), so it is "non vérifiée" here already. */
val DeliveryDraft.addressFixReason: GeocodeFailureReason?
    get() = addressFixReason(geocodeStatus, geocodeFailureReason)
        ?: GeocodeFailureReason.Unverified.takeIf { address.isNotBlank() && (latitude == null || longitude == null) }

/** R2 — this draft must be corrected or removed before going on (refused by HERE, or no address). */
val DeliveryDraft.needsAddressFix: Boolean
    get() = address.isBlank() || addressFixReason != null

fun DeliveryDraft.isLocated(): Boolean =
    address.isNotBlank() && geocodeStatus == GeocodeStatus.Geocoded && latitude != null && longitude != null
